"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TreeNode = void 0;
var jsx_runtime_1 = require("react/jsx-runtime");
var react_dnd_1 = require("react-dnd");
var react_1 = require("react");
var classnames_1 = __importDefault(require("classnames"));
var context_1 = require("./context");
var TreeNodeList_1 = require("./TreeNodeList");
var NodeInner_1 = require("./NodeInner");
var helpers_1 = require("./helpers");
var TreeNode = function (_a) {
    var node = _a.node, index = _a.index, level = _a.level, parent = _a.parent, parentChecked = _a.parentChecked;
    var ref = (0, react_1.useRef)(null);
    var _b = (0, context_1.useTreeContext)(), onMove = _b.onMove, size = _b.size, canDrag = _b.canDrag, canDrop = _b.canDrop, indicator = _b.indicator;
    var _c = (0, context_1.useExpandedState)(node), isExpanded = _c.isExpanded, isExpandable = _c.isExpandable;
    var _d = (0, context_1.useCheckableNode)(node, parentChecked), isChildChecked = _d.isChildChecked, isChecked = _d.isChecked;
    var nodeApi = (0, react_1.useMemo)(function () { return ({ data: node, index: index, parent: parent, id: node.id }); }, [node, index, parent]);
    var _e = (0, context_1.useNodeActions)(nodeApi), onClickNode = _e.onClickNode, onKeyDown = _e.onKeyDown;
    var _f = (0, react_1.useState)(), hoverAbove = _f[0], setHoverAbove = _f[1];
    var _g = (0, react_dnd_1.useDrag)(function () { return ({
        type: 'NODE',
        item: nodeApi,
        canDrag: typeof canDrag === 'function'
            ? function () {
                return canDrag(node);
            }
            : canDrag,
        collect: function (monitor) { return ({
            isDragging: monitor.isDragging(),
        }); },
    }); }, [nodeApi]), _dragSource = _g[0], drag = _g[1];
    var _h = (0, react_dnd_1.useDrop)(function () { return ({
        accept: 'NODE',
        collect: function (monitor) { return ({
            showLine: monitor.isOver() && monitor.canDrop(),
        }); },
        canDrop: function (item, monitor) {
            var _a, _b;
            if (!canDrop)
                return true;
            if (ref.current) {
                var above = (0, helpers_1.isCursorTop)(monitor, ref.current.getBoundingClientRect());
                var treeMoveArgs = (0, helpers_1.getTreeMoveArgs)(item, nodeApi, above);
                if (item.index === treeMoveArgs.newIndex &&
                    ((_a = treeMoveArgs.newParent) === null || _a === void 0 ? void 0 : _a.id) === ((_b = item.parent) === null || _b === void 0 ? void 0 : _b.id)) {
                    return false;
                }
                return canDrop(treeMoveArgs);
            }
            return false;
        },
        hover: function (item, monitor) {
            if (!monitor.canDrop()) {
                setHoverAbove(undefined);
                return;
            }
            if (item.id === node.id && hoverAbove !== undefined)
                return setHoverAbove(undefined);
            if (ref.current) {
                var cursorAbove = (0, helpers_1.isCursorTop)(monitor, ref.current.getBoundingClientRect());
                if (cursorAbove !== hoverAbove) {
                    setHoverAbove(cursorAbove);
                }
            }
            else {
                setHoverAbove(undefined);
            }
        },
        drop: function (item, monitor) {
            var didDrop = monitor.didDrop();
            if (didDrop) {
                return;
            }
            if (item.id === node.id)
                return;
            if (ref.current) {
                var above = (0, helpers_1.isCursorTop)(monitor, ref.current.getBoundingClientRect());
                onMove === null || onMove === void 0 ? void 0 : onMove((0, helpers_1.getTreeMoveArgs)(item, nodeApi, above));
            }
        },
    }); }, [node, parent, index]), showLine = _h[0].showLine, drop = _h[1];
    drag(drop(ref));
    var className = (0, classnames_1.default)('tree-node d-flex align-items-center w-100', size === 'sm' && 'tree-node-sm', node.disabled && 'tree-node-disabled', !isExpandable && 'tree-node-focusable', showLine && hoverAbove !== undefined && "tree-is-".concat(hoverAbove ? 'above' : 'below'), indicator && 'position-relative');
    var commonAriaProps = {
        'aria-level': level,
        'aria-disabled': node.disabled,
        'aria-checked': isChecked,
    };
    var treeNodeId = "tree-node-".concat(node.id);
    var nodeInner = ((0, jsx_runtime_1.jsx)(NodeInner_1.NodeInner, { node: nodeApi, level: level, isChecked: isChecked, isChildChecked: isChildChecked }));
    if (isExpandable && node.children) {
        return ((0, jsx_runtime_1.jsxs)("li", __assign({ "data-tree-item-id": node.id, role: "treeitem", className: "tree-node-focusable", "aria-expanded": isExpanded, tabIndex: -1, onKeyDown: onKeyDown, "data-testid": treeNodeId }, commonAriaProps, { children: [(0, jsx_runtime_1.jsx)("button", __assign({ ref: ref, className: className, "data-testid": "parent-node-button-".concat(node.id), onClick: function () { return onClickNode(node); }, tabIndex: -1 }, { children: nodeInner })), isExpanded && ((0, jsx_runtime_1.jsx)(TreeNodeList_1.TreeNodeList, { parent: nodeApi, nodes: node.children, level: level, parentChecked: isChecked }))] })));
    }
    if (node.href) {
        return ((0, jsx_runtime_1.jsx)("a", __assign({ ref: ref, "data-tree-item-id": node.id, href: node.href, target: node.target, className: className, role: "treeitem", tabIndex: -1, onKeyDown: onKeyDown, "data-testid": treeNodeId }, commonAriaProps, { children: nodeInner })));
    }
    return ((0, jsx_runtime_1.jsx)("button", __assign({ ref: ref, className: className, "data-tree-item-id": node.id, role: "treeitem", tabIndex: -1, onKeyDown: onKeyDown, onClick: function () { return onClickNode(node); }, "data-testid": treeNodeId }, commonAriaProps, { children: nodeInner })));
};
exports.TreeNode = TreeNode;
